import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

/**
 * Two guarantees that must hold across every route change, for the mobile nav
 * drawer and for any overlay that was open when navigation started.
 *
 * ── Why (R9) ────────────────────────────────────────────────────────────────
 *
 * "I'm on the Chat tab, go to the Project tab, then swipe back. Instead of the
 * Chat tab I see the sidebar menu open with Chat selected, and I cannot select
 * another menu item — screen frozen for a while, then the sidemenu disappears
 * and I see the chat tab."
 *
 * There are two separate things in that sentence and they have different causes.
 *
 * The drawer he SEES is almost certainly not a real drawer. iOS renders a
 * swipe-back by first showing its cached screenshot of the previous history
 * entry as it looked when he left it — and he left /chat by opening the drawer
 * and tapping Projects, so the snapshot is "drawer open over Chat". Nothing in
 * React is rendering it. It disappears when the app repaints, which is exactly
 * what he describes happening at the end.
 *
 * The part he CANNOT INTERACT WITH is real. Radix puts `pointer-events: none` on
 * document.body while a dismissable layer is open and removes it on teardown. If
 * the layer unmounts mid-transition — a route change during the sheet's exit
 * animation, an animationend that never fires because the gesture interrupted it
 * — that teardown can be skipped, and the body stays inert. Every tap does
 * nothing until something else clears it. That is the freeze, and it is why the
 * frozen thing looks like a menu that will not respond.
 *
 * So this hook does two things, and only the second is a real fix; the first
 * makes the snapshot honest.
 *
 *  1. Close the drawer on every route change. The click handler already closes
 *     it, but that only covers taps on nav links — not programmatic navigation,
 *     not back/forward, not a deep link. Keying on the pathname means drawer
 *     state can never be live across a navigation, so the snapshot iOS caches on
 *     the way out is never "drawer open" either.
 *
 *  2. Release leaked body inertness once no overlay is left open. Guarded on an
 *     actually-open dialog still being in the DOM, so a legitimately open
 *     overlay keeps its own `pointer-events: none`.
 *
 * @param closeOverlays called on each route change to drop overlay state
 */
export function useOverlayRouteReset(closeOverlays: () => void): void {
  const { pathname } = useLocation()
  const mounted = useRef(false)

  useEffect(() => {
    // Closing is skipped on the FIRST run: this effect fires on mount as well as
    // on navigation, and a caller that legitimately mounts with an overlay open
    // — a `?open=` deep link, say — would have it shut before it was ever seen.
    // Only an actual route CHANGE should close anything.
    if (mounted.current) closeOverlays()
    mounted.current = true

    // The body release, by contrast, is worth doing on mount too: arriving on a
    // page with inertness left over from wherever we came from is exactly the
    // state this exists to undo.
    const release = () => {
      // A dialog that is genuinely open still needs the body inert. Radix marks
      // its content `data-state="open"`; a layer mid-exit is "closed" already,
      // which is the case we DO want to clean up after.
      const stillOpen = document.querySelector(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
      )
      if (stillOpen) return

      if (document.body.style.pointerEvents === 'none') {
        document.body.style.removeProperty('pointer-events')
      }
      // react-remove-scroll's marker. Left behind, it keeps the scroll lock's
      // stylesheet applying to a page with nothing open.
      document.body.removeAttribute('data-scroll-locked')
    }

    release()
    // Radix's own teardown may run after this effect. One frame later catches
    // the case where it ran and correctly restored things (no-op), and the case
    // where it never ran at all (the leak).
    const frame = requestAnimationFrame(release)
    return () => cancelAnimationFrame(frame)
    // closeOverlays is intentionally not a dependency: callers pass an inline
    // arrow, and including it would run this on every render rather than on
    // navigation, closing overlays the user just opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])
}
